// src/i18n/useStrongTypeTranslation.ts
import { useI18n } from "vue-i18n";
import type { TranslationKey } from "./types";

type TranslationParams = Record<string, string | number>;

export function useStrongTypedI18n() {
  const i18n = useI18n();

  // i18n.te() only checks the active locale; also check the en-AU fallback so
  // keys that live only in en-AU (which t() still resolves) aren't reported missing.
  const keyExists = (key: TranslationKey): boolean => i18n.te(key) || i18n.te(key, "en-AU");

  /**
   * Strongly-typed translation function
   * @param key - Translation key (e.g., 'common.actions', 'pages.home.header.actions.logout')
   * @param params - Optional parameters for interpolation
   * @param plural - The count that picks a pluralised message's form. Named
   *   values come second and the count third, as vue-i18n reads them; a count
   *   left off renders the first form whatever the number.
   */
  const t = (key: TranslationKey, params?: TranslationParams, plural?: number): string => {
    if (import.meta.env.DEV && !keyExists(key)) {
      console.error(`❌ Missing translation key: "${key}"`);
    }
    if (plural !== undefined) return i18n.t(key, params ?? {}, plural);
    return i18n.t(key, params);
  };

  /**
   * Check if translation key exists
   */
  const te = (key: TranslationKey): boolean => {
    return keyExists(key);
  };

  /**
   * Get translation message (returns object for nested keys)
   */
  const tm = (key: TranslationKey) => {
    return i18n.tm(key);
  };

  return {
    t,
    te,
    tm,
    d: i18n.d,
    n: i18n.n,
    locale: i18n.locale,
    rt: i18n.rt,
  };
}
